import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
  setSettlementSummary,
  clearSettlementState,
  setShowSettlementModal,
  setShowGeneralSettlementModal,
} from '../../store/slices/settlements/productSettlementSlice';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import ProductSettlementService from '../../services/purchases/ProductSettlementService';
import useConfirmModal from '../useConfirmModal';
import useModals from '../useModals';
import useApiErrorHandler from '../useApiErrorHandler';
import { updateSettledQuantitiesBatch, updateSettledQuantityByProductId } from '../../store/slices/purchases/basePurchaseSlice';
import InventoryEntryService from '../../services/inventory/InventoryEntryService';
import { useBasePurchase } from '../purchases/useBasePurchase';


export const useProductSettlement = () => {
  const dispatch: AppDispatch = useDispatch();
  const { show: showAlert } = useAlerts();
  const { openModal } = useModals();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirmModal();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handlerResetBasePurchase } = useBasePurchase();

  const { settlementSummary, generalSettlement, error, showGeneralSettlementModal, showSettlementModal } = useSelector(
    (state: RootState) => state.productSettlement
  );

  const {
    form
  } = useSelector((state: RootState) => state.basePurchase);

  const handlerSettleProduct = async (dto: any) => {
    try {
      showLoadingIndicator();

      const response = await ProductSettlementService.register(dto);
      console.log('📦 Respuesta de asentamiento desde el backend:', response);

      if (response?.statusCode === 201) {
        const { status, details } = response.data;

        // 🧠 Actualiza el resumen
        dispatch(
          setSettlementSummary({
            product: settlementSummary?.product!,
            status,
            details,
          })
        );

        // ✅ Actualiza la cantidad asentada en tiempo real
        if (status?.productId && status?.totalSettled !== undefined) {
          dispatch(
            updateSettledQuantityByProductId({
              productId: status.productId,
              settledQuantity: Number(status.totalSettled),
            })
          );
        }

        const { type, title, message } = response;
        openModal(type, { title, message });
      } else {
        showAlert('error', 'Error', 'No se pudo asentar el producto.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };


  const handlerSettleGeneral = async (purchaseId: number, branchId: number) => {
    try {
      showLoadingIndicator();
      const response = await ProductSettlementService.registerGeneral(purchaseId, branchId);
      console.log(response);
      if (response?.statusCode === 200) {
        const updates = response.data.map(item => ({
          productId: item.productId,
          settledQuantity: Number(item.totalSettled),
        }));

        dispatch(updateSettledQuantitiesBatch(updates));

        const { type, title, message } = response;
        openModal(type, { title, message });
      } else {
        showAlert('error', 'Error', 'No se pudo realizar el asentamiento general.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const handlerGetSettlementsByProduct = async (purchaseId: number, detailId: number) => {
    try {
      showLoadingIndicator();

      const response = await ProductSettlementService.getByProduct(purchaseId, detailId);
      console.log("Response", response);

      if (response?.statusCode === 200) {
        dispatch(setSettlementSummary(response.data));
        dispatch(setShowSettlementModal(true)); // ✅ abrimos modal sólo si fue exitoso
      } else {
        showAlert('error', 'Error', 'No se pudo obtener el resumen del producto.');
      }
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };


  const handlerDeleteSettlement = async (
    purchaseId: number,
    productId: number,
    branchId: number
  ) => {
    confirm({
      title: '¿Eliminar asentamiento?',
      message: '¿Estás seguro de que deseas eliminar este asentamiento? Esta acción no se puede deshacer.',
      icon: 'warning',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          showLoadingIndicator();

          const response = await ProductSettlementService.delete(
            purchaseId,
            productId,
            branchId
          );

          console.log('🗑️ Respuesta de eliminación:', response);

          if (response?.statusCode === 200) {
            const { status, details } = response.data;

            // 🧠 Actualiza el resumen visual
            dispatch(
              setSettlementSummary({
                product: settlementSummary?.product!,
                status,
                details,
              })
            );

            // ✅ Actualiza la cantidad asentada en tiempo real
            if (status?.productId && status?.totalSettled !== undefined) {
              dispatch(
                updateSettledQuantityByProductId({
                  productId: status.productId,
                  settledQuantity: Number(status.totalSettled),
                })
              );
            }

            const { type, title, message } = response;
            openModal(type, { title, message }); // ✅ éxito con modal
          } else {
            showAlert('error', 'Error', 'No se pudo eliminar el asentamiento.');
          }
        } catch (error: any) {
          handle(error, 'Ocurrió un error al eliminar el asentamiento.');
        } finally {
          hideLoadingIndicator();
        }
      },
    });
  };




  /**
   * Finaliza el proceso de asentamiento y registra los movimientos en inventario.
   *
   * 🔹 Muestra una confirmación al usuario.
   * 🔹 Si confirma, llama al servicio de inventario.
   * 🔹 Muestra mensajes del backend y redirige al historial.
   */
  const handlerFinalizeSettlement = async () => {
    confirm({
      title: '¿Desea finalizar el asentamiento de productos?',
      message:
        'Este es el paso final del proceso.\n\n' +
        'Al confirmar, los productos se trasladarán oficialmente al inventario de cada sucursal según lo asignado.\n\n' +
        'Esta acción es definitiva y no podrá deshacerse.',
      icon: 'warning',
      confirmText: 'Confirmar',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          showLoadingIndicator();

          const response = await InventoryEntryService.registerFromSettlement(form.id); // ✅ directo desde Redux
          // console.log('🚚 Asentamiento FINALIZADO:', response);
          const { type, title, message, statusCode } = response;

          if (statusCode === 200) {
            openModal(type, { title, message }); // ✅ éxito con modal
            handlerResetBasePurchase(); // ✅ redirige después del asentamiento
          } else {
            showAlert('error', 'Error al registrar inventario', response?.message || 'Error desconocido.');
          }

        } catch (error: any) {
          handle(error, 'Ocurrió un error al registrar el inventario.');
        } finally {
          hideLoadingIndicator();
        }
      },
    });
  };

  /**
   * 🧹 Limpia todos los datos del estado de asentamientos
   */
  const handlerClearSettlementState = () => {
    dispatch(clearSettlementState());
  };


  const handlerCloseSettlementModal = () => {
    dispatch(setShowSettlementModal(false));
  };

  const handlerOpenGeneralModal = () => {
    dispatch(setShowGeneralSettlementModal(true));
  };

  const handlerCloseGeneralModal = () => {
    dispatch(setShowGeneralSettlementModal(false));
  };


  return {
    // State
    settlementSummary,
    generalSettlement,
    error,
    showGeneralSettlementModal,
    showSettlementModal,

    // Handlers
    handlerSettleProduct,
    handlerSettleGeneral,
    handlerGetSettlementsByProduct,
    handlerClearSettlementState,

    handlerCloseSettlementModal,
    handlerOpenGeneralModal,
    handlerCloseGeneralModal,

    handlerDeleteSettlement,
    handlerFinalizeSettlement
  };
};
